import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'MyTypes';
import {
    compose,
    flatten,
    intersection,
    isEmpty,
    keyBy,
    mapValues,
    omit,
    pick,
    uniq,
    uniqBy,
} from 'lodash/fp';

import { NATIONAL_LEVEL, getLocationsForLevel } from '@portal/common/components';
import {
    TYPE_ARROW_CHART,
    TYPE_DATA_TABLE_CHART,
    TYPE_LINE_CHART,
    TYPE_MAP_CHART,
    TYPE_SCATTER_MAP_CHART,
    TYPE_TREEMAP_CHART,
} from '@portal/common/models/chart-type';
import {
    COMORBID_CAUSE_ID_KEY,
    DATA_TYPE_KEY,
    FORECAST_SCENARIO_ID_KEY,
    LOCATION_ID_KEY,
    MAP_DETAIL_LEVEL_ID_KEY,
    MAP_TYPE_ID_KEY,
    METRIC_ID_KEY,
    OUTCOME_ID_KEY,
    PRIMARY_ENTITY_ID_KEY,
    PRIMARY_ENTITY_ID_LOCALIZATION_KEY,
    PRIMARY_ENTITY_KEY,
    ROUND_ID_KEY,
    YEAR_KEY,
    isTimeUnitKey,
} from '@portal/common/models/data-key';
import { DataProjectId } from '@portal/common/models/data-project';
import {
    AllDataTypes,
    CauseOutcome,
    HealthThreatsMetricsRecords,
    PopulationRecords,
    TakedaDengueRecords,
    getCompatibleDataTypes,
    getDataTypePrimaryEntityKey,
} from '@portal/common/models/data-type';
import { getDataTypeConfig } from '@portal/common/models/data-type-config';
import {
    DALYs,
    Deaths,
    Incidence,
    Prevalence,
    YLDs,
    YLLs,
} from '@portal/common/models/measure-types';
import { getAvailableChartTypes } from '@portal/common/models/organization';
import {
    AgeGroupEntity,
    AgeGroupsOrder,
    CauseEntity,
    ChartType,
    ConditionDetailNotesRecord,
    ConditionRestrictionRecord,
    DataCondition,
    DataFilters,
    DataGranularityKey,
    DataKey,
    DataResponse,
    DataTableDisplayState,
    DataToolConfig,
    DataType,
    ForecastDataCalculation,
    LocationEntity,
} from '@portal/common/types';
import {
    mapValueToCondition,
    mergeAndSortDataResponses,
} from '@portal/common/utility/chart-data-helpers';
import {
    getConditionsDataTypes,
    hasErrorInRefinementFilters,
} from '@portal/common/utility/filters-helpers';
import { getNamesById } from '@portal/common/utility/get-names-by-id';

import {
    fillMissingFiltersWithDefaults,
    intersectRefinementFiltersWithGranularity,
    removeFiltersNotInGranularity,
} from '../../components/DataExplorer/utils';
import config from '../../config';
import { conditionComparisonFunc } from '../../models/data-condition';
import { getSelectedDatasetsConfiguration } from '../../models/data-explorer-config';
import { DATA_TOOL_CONFIG_BY_TOOL_PATH, getDataProjectIdsForTool } from '../../models/data-tool';
import { getDatasetConditions, getDatasetsPrimaryEntityFilterKeys } from '../../models/dataset';
import { DENGUE_SCENARIOS_PATH } from '../../router/paths';
import { getMergedDataCollectionGranularity } from '../../utility/data-loader';
import {
    CombinedFiltersCountInputs,
    CombinedFiltersCountOptions,
    CombinedFiltersCountOverrides,
    countCombinedFilters,
} from '../../utility/data/calculate-combined-filters-count';
import { calculateNowcastYears, getNowcastsStartYear } from '../../utility/forecast-data-helpers';
import { getLocationChildrenById } from '../entity-hierarchies/selectors';
import { getOrganization } from '../organization/selectors';
import {
    getSelectedDataCollectionForDataTool,
    getSelectedDataTool,
} from '../user-settings/selectors';
import { getLicensedConditionChildren, getLicensedConditionsByLevel } from './utils';

export const getAgeGroups = (state: RootState): AgeGroupEntity[] | null =>
    state.dataExplorer.ageGroups;

export const getAgeGroupsOrder = createSelector(
    getAgeGroups,
    (ageGroups): AgeGroupsOrder | null => {
        if (ageGroups == null) {
            return null;
        }

        return compose(mapValues('order'), keyBy('id'))(ageGroups) as AgeGroupsOrder;
    }
);

const createMergedDataResponsesSelector = <I, O>(
    inputSelector: (state: RootState) => I,
    resultFunction: (input: I) => O
) => {
    return createSelector([inputSelector], resultFunction, {
        memoizeOptions: {
            equalityCheck: (newArgs, lastArgs) => {
                const [newInput] = newArgs;
                const [prevInput] = lastArgs;

                const propsToCompare = [
                    'dataResponsesByType',
                    'conditions',
                    'dataTypes',
                    'refinementFilters',
                    'granularity',
                    'samplePopulationSize',
                ];
                const newProps = pick(newInput, propsToCompare);
                const prevProps = pick(prevInput, propsToCompare);

                return JSON.stringify(newProps) === JSON.stringify(prevProps);
            },
        },
    });
};
export const getDataResponsesByType = (state: RootState) => state.dataExplorer.dataResponsesByType;

export const getMergedDataResponses = createMergedDataResponsesSelector(
    getDataResponsesByType,
    (dataResponsesByType): DataResponse | null => mergeAndSortDataResponses(dataResponsesByType)
);

export const getSelectedConditions = (state: RootState): DataCondition[] =>
    state.dataExplorer.selectedConditions;

export const getQueriedConditions = (state: RootState): DataCondition[] =>
    state.dataExplorer.queriedConditions;

export const getSelectedRefinementFilters = (state: RootState): DataFilters | null =>
    state.dataExplorer.selectedRefinementFilters;

export const getQueriedRefinementFilters = (state: RootState): DataFilters | null =>
    state.dataExplorer.queriedRefinementFilters;

export const getQueriedDataSettings = (state: RootState) => state.dataExplorer.queriedDataSettings;

export const getSingleYearAgeGranularity = (state: RootState) =>
    state.dataExplorer.singleYearAgeGranularity;

export const getDataRecordsCount = createSelector(getMergedDataResponses, (responses) => {
    return responses?.records?.length || 0;
});

export const getChartDataResponsesByType = (state: RootState) =>
    state.dataExplorer.chartDataResponsesByType;

export const getMergedChartDataResponses = createSelector(
    getChartDataResponsesByType,
    (dataResponsesByType): DataResponse | null => mergeAndSortDataResponses(dataResponsesByType)
);

export const getSelectedDataProject = createSelector(
    getSelectedDataCollectionForDataTool,
    (dataCollection) => {
        return dataCollection?.data_project_id;
    }
);

export const getSelectedConditionsDataTypes = createSelector(getSelectedConditions, (conditions) =>
    isEmpty(conditions) ? null : getConditionsDataTypes(conditions)
);

export const getSelectedDatasetsConfig = createSelector(
    getSelectedConditionsDataTypes,
    getSelectedDataCollectionForDataTool,
    (selectedConditionsDataTypes, selectedDataCollection) => {
        return getSelectedDatasetsConfiguration(
            selectedConditionsDataTypes,
            selectedDataCollection
        );
    }
);

export const getDataToolConfig = createSelector(
    getSelectedDataTool,
    getSelectedDataProject,
    getSelectedConditionsDataTypes,
    (selectedDataTool, selectedDataProjectId, dataTypes) => {
        const dataType = dataTypes?.[0];
        const dataToolConfig = DATA_TOOL_CONFIG_BY_TOOL_PATH[selectedDataTool] || {};
        const dataProjectConfig = selectedDataProjectId
            ? DATA_TOOL_CONFIG_BY_TOOL_PATH[selectedDataTool]?.overwritesByDataProjectId?.[
                  selectedDataProjectId
              ]
            : {};
        let mergedConfig: DataToolConfig = { ...dataToolConfig, ...dataProjectConfig };
        const dataTypeConfig = dataType ? mergedConfig.overwritesByDataType?.[dataType] : {};
        mergedConfig = { ...mergedConfig, ...dataTypeConfig };
        return omit(['overwritesByDataProjectId', 'overwritesByDataType'], mergedConfig);
    }
);

export const getSelectedConditionsDataTypesGranularity = createSelector(
    getSelectedDataCollectionForDataTool,
    getSelectedConditionsDataTypes,
    (selectedDataCollectionForDataTool, selectedConditionsDataTypes) =>
        getMergedDataCollectionGranularity(
            selectedDataCollectionForDataTool,
            selectedConditionsDataTypes
        )
);

export const getQueriedConditionsDataTypes = createSelector(getQueriedConditions, (conditions) =>
    isEmpty(conditions) ? null : getConditionsDataTypes(conditions)
);

export const getQueriedConditionsDataTypesGranularity = createSelector(
    getSelectedDataCollectionForDataTool,
    getQueriedConditionsDataTypes,
    (selectedDataCollectionForDataTool, queriedConditionsDataTypes) =>
        getMergedDataCollectionGranularity(
            selectedDataCollectionForDataTool,
            queriedConditionsDataTypes
        )
);

export const getDataToolSupportedCharts = createSelector(
    getDataToolConfig,
    getSelectedDataProject,
    getSelectedConditionsDataTypesGranularity,
    getOrganization,
    getSelectedDatasetsConfig,
    (dataToolConfig, selectedDataProject, granularity, organization, dataset) => {
        const availableChartTypes = [...getAvailableChartTypes(organization)];

        let supportedCharts: ChartType[] = [];
        if (dataToolConfig?.supportedCharts) {
            supportedCharts = dataToolConfig.supportedCharts;
        }

        if (selectedDataProject === DataProjectId.RACE_ETHNICITY) {
            const timeUnitKey = dataset?.time_unit_field;
            if (timeUnitKey && granularity?.[timeUnitKey]?.length > 1) {
                supportedCharts = uniq([TYPE_LINE_CHART, ...supportedCharts]);
            }
        }

        if (supportedCharts.includes(TYPE_DATA_TABLE_CHART)) {
            availableChartTypes.push(TYPE_DATA_TABLE_CHART);
        }
        if (availableChartTypes.includes(TYPE_MAP_CHART)) {
            availableChartTypes.push(TYPE_SCATTER_MAP_CHART);
        }

        return intersection(availableChartTypes, supportedCharts);
    }
);

export const getDataToolDefaultMapType = createSelector(
    getDataToolConfig,
    (dataToolConfig) =>
        dataToolConfig?.defaultChartFilters?.[MAP_TYPE_ID_KEY] ?? config.globalLocationId
);

export const getSamplePopulationSize = (state: RootState): number =>
    state.dataExplorer.samplePopulationSize;

export const getSelectedConditionsPrimaryEntityFilters = createSelector(
    getSelectedDataCollectionForDataTool,
    getSelectedConditionsDataTypes,
    getDatasetsPrimaryEntityFilterKeys
);

export const getSelectedConditionsDataTypesDefaultFilters = createSelector(
    getSelectedConditionsDataTypesGranularity,
    getSelectedDatasetsConfig,
    getDataToolConfig,
    getSelectedConditionsPrimaryEntityFilters,
    (
        selectedConditionsDataTypesGranularity,
        datasetConfig,
        dataToolConfig,
        primaryEntityFilters
    ) => {
        const defaultFilters = removeFiltersNotInGranularity(
            omit(primaryEntityFilters, datasetConfig.defaultFilters),
            selectedConditionsDataTypesGranularity
        );

        const filtersToSkip = uniq([
            ...(dataToolConfig.filtersSkipDefaultSelection || []),
            ...primaryEntityFilters,
        ]);

        return fillMissingFiltersWithDefaults(
            defaultFilters,
            selectedConditionsDataTypesGranularity,
            filtersToSkip
        );
    }
);

export const getSelectedRefinementFiltersWithFallbackToDefaultFilters = createSelector(
    getSelectedRefinementFilters,
    getSelectedConditionsDataTypesDefaultFilters,
    getDataToolConfig,
    (selectedRefinementFilters, defaultFilters, { filtersSkipDefaultSelection }) =>
        mergeFiltersWithFallbackToDefaultFilters(
            defaultFilters,
            selectedRefinementFilters,
            filtersSkipDefaultSelection
        )
);

// the store's defaults fallback, over a hypothetical selection
export const applyDefaultFiltersFallback = (
    state: RootState,
    refinementFilters: DataFilters | null
): DataFilters =>
    mergeFiltersWithFallbackToDefaultFilters(
        getSelectedConditionsDataTypesDefaultFilters(state),
        refinementFilters,
        getDataToolConfig(state).filtersSkipDefaultSelection
    );

export const getRoundId = createSelector(
    getSelectedRefinementFiltersWithFallbackToDefaultFilters,
    (selectedRefinementFilters) => {
        const roundId = selectedRefinementFilters
            ? selectedRefinementFilters[ROUND_ID_KEY]
            : removeFiltersNotInGranularity()[ROUND_ID_KEY];

        // Remove parseInt once selected filters refactored to reflect granularity option types
        return parseInt(roundId);
    }
);

export const getIsSingleYearAgeDataSuiteSupported = createSelector(getRoundId, (roundId): boolean =>
    config.singleYearAgeSupportedDataSuites.includes(roundId)
);

export const getSelectedDatasetForecastDataCalculationMap = createSelector(
    getSelectedConditionsDataTypes,
    getSelectedDataCollectionForDataTool,
    getRoundId,
    (selectedConditionsDataTypes, selectedDataCollection, roundId) => {
        if (
            !selectedDataCollection ||
            !selectedConditionsDataTypes?.length ||
            !roundId ||
            isNaN(roundId)
        ) {
            return null;
        }

        const dataset = selectedDataCollection.datasets.find((dataset) =>
            selectedConditionsDataTypes.includes(dataset.data_type)
        );

        const forecastDataCalculationMap = dataset?.forecast_data_calculation_map;
        if (!forecastDataCalculationMap) {
            return null;
        }

        const roundIdKey = String(roundId);
        return forecastDataCalculationMap[roundIdKey] || null;
    }
);

export const getIncludeNowcasts = (state: RootState) => state.dataExplorer.includeNowcasts;
export const getPdfReportSend = (state: RootState) => state.dataExplorer.pdfReportSend;
export const getOrganizationForecastDataCalculationIsAllowed = (state: RootState) =>
    state.organization?.forecast_data_calculation_is_allowed;
export const getOrganizationForecastDataCalculationYears = (state: RootState) =>
    state.organization?.forecast_data_calculation_years;

export const getSelectedChartType = (state: RootState) => state.dataExplorer.selectedChartType;

export const getSelectedDataType = (state: RootState) => state.dataExplorer.selectedDataType;

export const getDataCollections = (state: RootState) => state.dataExplorer.dataCollections;

export const getInitializationKey = (state: RootState) => state.dataExplorer.initializationKey;

export const getDataToolDataCollections = createSelector(
    getDataCollections,
    getSelectedDataTool,
    (allCollections, selectedDataTool) => {
        const projectIds = getDataProjectIdsForTool(selectedDataTool);
        if (!(allCollections && allCollections.length && projectIds && projectIds.length)) {
            return allCollections;
        }
        return allCollections.filter((collection) =>
            projectIds.includes(collection.data_project_id)
        );
    }
);

export const getDataCollectionNamesById = createSelector(getDataToolDataCollections, getNamesById);

export const getChartSelectedRefinementFilters = (state: RootState) =>
    state.dataExplorer.chartFilters;

export const getPreservedChartFilters = (state: RootState) =>
    state.dataExplorer.preservedChartFilters;

const createMetricSelectedSelector = (metricId: number) =>
    createSelector(
        getChartSelectedRefinementFilters,
        getQueriedRefinementFilters,
        (chartFilters, queriedRefinementFilters) => {
            const selectedMetrics =
                chartFilters?.[METRIC_ID_KEY] || queriedRefinementFilters?.[METRIC_ID_KEY];
            return selectedMetrics?.includes(metricId);
        }
    );

export const getIsPercentMetricSelected = createMetricSelectedSelector(config.percentMetricId);

export const getIsNumberMetricSelected = createMetricSelectedSelector(config.numberMetricId);

export const getIsRateMetricSelected = createMetricSelectedSelector(config.rateMetricId);

export const isDataLoading = (state: RootState) => state.dataExplorer.isDataLoading;

export const isChartDataLoading = (state: RootState) => state.dataExplorer.isChartDataLoading;

export const getMergedData = (state: RootState) => {
    // TODO
};

export const getMergedDataWithDataTypeColumn = (state: RootState) => {
    // TODO
};

export const getConditionDetailNotes = (state: RootState): ConditionDetailNotesRecord[] | null =>
    state.dataExplorer.conditionDetailNotes;

export const getDataTableDataResponse = createSelector(
    getMergedDataResponses,
    getConditionDetailNotes,
    getDataToolConfig,
    (responses, conditionDetailNotes, dataToolConfig) => {
        const { records, columns } = responses;
        if (
            !dataToolConfig?.dataTableAdditionalColumns ||
            !conditionDetailNotes ||
            isEmpty(records) ||
            isEmpty(columns)
        ) {
            return responses;
        }
        const primaryEntityIdIdx = columns.indexOf(PRIMARY_ENTITY_ID_KEY);
        const noPrimaryEntityId = 0;
        const cdnDataByEntityId = conditionDetailNotes.reduce(
            (acc, current: ConditionDetailNotesRecord) => {
                const cdnKeys = current.records.map((record) => record.key.split(':')[0]);
                const entityId = !~primaryEntityIdIdx
                    ? noPrimaryEntityId
                    : current.metaRecords.find((record) => record.key === PRIMARY_ENTITY_ID_KEY)
                          ?.value;
                acc[entityId] = dataToolConfig?.dataTableAdditionalColumns.map((column) => {
                    const keyIdx = cdnKeys.indexOf(column);
                    return ~keyIdx ? current.records[keyIdx].value : null;
                });
                return acc;
            },
            {}
        );

        return {
            records: records.map((record) => {
                const entityId = record[primaryEntityIdIdx];
                const cdnData = cdnDataByEntityId[entityId ?? noPrimaryEntityId];
                return [...record, ...(Array.isArray(cdnData) ? cdnData : [])];
            }),
            columns: [...columns, ...dataToolConfig.dataTableAdditionalColumns],
        };
    }
);

export const getQueriedConditionsPrimaryEntityFilters = createSelector(
    getSelectedDataCollectionForDataTool,
    getQueriedConditionsDataTypes,
    getDatasetsPrimaryEntityFilterKeys
);

export const getSelectedConditionsCompatibleDataTypes = createSelector(
    getSelectedConditionsDataTypes,
    (selectedConditionsDataTypes): DataType[] | null =>
        getCompatibleDataTypes(selectedConditionsDataTypes)
);

// used for initialization default selectedRefinedGranularity
export const getDefaultFiltersByConditions =
    (conditions: DataCondition[]) => (state: RootState) => {
        const dataCollection = getSelectedDataCollectionForDataTool(state);
        const dataTypes = uniq(conditions.map((i) => i.data_type));

        const primaryEntityFilters = getDatasetsPrimaryEntityFilterKeys(dataCollection, dataTypes);

        return fillMissingFiltersWithDefaults(
            removeFiltersNotInGranularity(
                omit(
                    primaryEntityFilters,
                    getSelectedDatasetsConfigByConditions(conditions)(state).defaultFilters
                ),
                getMergedDataCollectionGranularity(dataCollection!, dataTypes)
            ),
            getSelectedConditionsDataTypesGranularity(state),
            uniq([
                ...(getDataToolConfig(state).filtersSkipDefaultSelection || []),
                ...primaryEntityFilters,
            ])
        );
    };

// no fallback: unlike the selector below, this is empty until granularity loads
export const getStoredConditionsRefinedGranularity = (state: RootState) =>
    state.dataExplorer.selectedConditionsRefinedGranularity;

export const getSelectedConditionsRefinedGranularity = (state: RootState) => {
    const refinedGranularity = getStoredConditionsRefinedGranularity(state);
    // Treat an empty object as "no refined granularity" so we fall back to the
    // dataset granularity. Without this, an empty refined granularity makes
    // downstream intersections drop every filter
    return !isEmpty(refinedGranularity)
        ? refinedGranularity
        : getSelectedConditionsDataTypesGranularity(state);
};

export const getSelectedConditionsLastPublishedYear = createSelector(
    getSelectedConditionsRefinedGranularity,
    (refinedGranularity): number | null => {
        const publishedYears = refinedGranularity?.[YEAR_KEY] as number[] | undefined;
        return isEmpty(publishedYears) ? null : Math.max(...publishedYears);
    }
);

export const getNowcastYears = createSelector(
    getSelectedDatasetForecastDataCalculationMap,
    getOrganizationForecastDataCalculationYears,
    getSelectedConditionsLastPublishedYear,
    calculateNowcastYears
);

export const getHasNowcastYearsBeyondPublished = createSelector(
    getSelectedConditionsLastPublishedYear,
    getNowcastYears,
    (lastPublishedYear, nowcastYears): boolean =>
        !isEmpty(nowcastYears) &&
        lastPublishedYear != null &&
        nowcastYears[nowcastYears.length - 1] > lastPublishedYear
);

const checkIsNowcastsActive = (
    isNowcastsEnabled: boolean,
    includeNowcasts: boolean,
    forecastDataCalculationMap: ForecastDataCalculation | null,
    hasNowcastYearsBeyondPublished: boolean
): boolean => {
    return Boolean(
        isNowcastsEnabled &&
        includeNowcasts &&
        forecastDataCalculationMap &&
        hasNowcastYearsBeyondPublished
    );
};

export const getIsNowcastsActive = createSelector(
    getOrganizationForecastDataCalculationIsAllowed,
    getIncludeNowcasts,
    getSelectedDatasetForecastDataCalculationMap,
    getHasNowcastYearsBeyondPublished,
    checkIsNowcastsActive
);

export const getIsNowcastsActiveWhenIncluded = (
    state: RootState,
    includeNowcasts: boolean
): boolean =>
    checkIsNowcastsActive(
        getOrganizationForecastDataCalculationIsAllowed(state),
        includeNowcasts,
        getSelectedDatasetForecastDataCalculationMap(state),
        getHasNowcastYearsBeyondPublished(state)
    );

export const getQueriedConditionsRefinedGranularity = createSelector(
    (state: RootState) => state.dataExplorer?.queriedConditionsRefinedGranularity,
    getQueriedConditionsDataTypesGranularity,
    (queriedRefinedGranularity, queriedDataTypesGranularity) =>
        !isEmpty(queriedRefinedGranularity)
            ? queriedRefinedGranularity
            : queriedDataTypesGranularity
);

export const getQueriedRefinementFiltersWithFallbackToDefaultFilters = createSelector(
    getQueriedRefinementFilters,
    getSelectedConditionsDataTypesDefaultFilters,
    getDataToolConfig,
    (refinementFilters, defaultFilters, { filtersSkipDefaultSelection }): DataFilters =>
        mergeFiltersWithFallbackToDefaultFilters(
            defaultFilters,
            refinementFilters,
            filtersSkipDefaultSelection
        )
);

export const mergeFiltersWithFallbackToDefaultFilters = (
    defaultFilters,
    refinementFilters,
    filtersSkipDefaultSelection
) => {
    const filterKeys = uniq(Object.keys({ ...defaultFilters, ...refinementFilters }));

    const filters = {};
    filterKeys.forEach((key) => {
        filters[key] = (refinementFilters && refinementFilters[key]) || null;

        if (filters[key] == null && !(filtersSkipDefaultSelection || []).includes(key)) {
            filters[key] = defaultFilters[key];
        }
    });

    return filters;
};

export const getNormalizedQueriedRefinementFilters = createSelector(
    getQueriedRefinementFiltersWithFallbackToDefaultFilters,
    getQueriedConditionsRefinedGranularity,
    intersectRefinementFiltersWithGranularity
);

export const getNormalizedSelectedRefinementFilters = createSelector(
    getSelectedRefinementFilters,
    getSelectedConditionsRefinedGranularity,
    intersectRefinementFiltersWithGranularity
);

export const getSelectedRefinementFiltersWithoutPrimaryEntities = createSelector(
    getSelectedConditions,
    getSelectedRefinementFilters,
    (conditions, refinementFilters): DataFilters => {
        const dataTypes = uniq(conditions.map(({ data_type }) => data_type));
        const primaryEntityKeys = dataTypes.map(
            (dataType) => getDataTypeConfig()[dataType].filterKey
        );

        return omit(primaryEntityKeys, refinementFilters);
    }
);

const getVisibleRefinementFiltersRaw = (state: RootState) =>
    state.dataExplorer.visibleRefinementFilters;

export const getVisibleRefinementFilters = createSelector(
    getVisibleRefinementFiltersRaw,
    getSelectedConditionsDataTypesGranularity,
    (visibleFilters, selectedConditionsDataTypesGranularity) => {
        const granularityKeys = Object.keys(selectedConditionsDataTypesGranularity);

        return intersection(visibleFilters, granularityKeys);
    }
);

export const getChartDimensionFilter = (state: RootState): DataKey | null =>
    state.dataExplorer.chartDimensionFilter;

export const isArrowChartDrillable = createSelector(
    getSelectedConditionsDataTypes,
    getChartDimensionFilter,
    (dataTypes: DataType[] = [], chartDimensionFilter: DataGranularityKey): boolean => {
        const isSingleDataTypeSelected = dataTypes?.length === 1;
        const isSupportedDataType = dataTypes?.includes(CauseOutcome);
        const isConditionDimension = chartDimensionFilter === PRIMARY_ENTITY_KEY;

        return isSingleDataTypeSelected && isSupportedDataType && isConditionDimension;
    }
);

export const getDrillIntoCondition = (state: RootState): DataCondition | null =>
    state.dataExplorer.drillIntoCondition;

export const getEntityHierarchies = (state: RootState) => state.entityHierarchies;

export const getRefinedCausesHierarchy = createSelector(
    getSelectedConditionsDataTypesGranularity,
    getEntityHierarchies,
    (granularity, entityHierarchies): CauseEntity[] => {
        const primaryEntityKey = getDataTypePrimaryEntityKey(CauseOutcome);
        const granularityForKey = granularity?.[primaryEntityKey] || [];
        const hierarchyKey = getDataTypeConfig()[CauseOutcome].entityHierarchyKey;
        const hierarchy = entityHierarchies[hierarchyKey] || [];
        return hierarchy.filter(({ id }) => granularityForKey.includes(id));
    }
);

export const getLicensedLevel2Causes = createSelector(
    getRefinedCausesHierarchy,
    (refinedHierarchy) => {
        return getLicensedConditionsByLevel(CauseOutcome, refinedHierarchy, 2);
    }
);

export const getLicensedDrillToConditions = createSelector(
    getDrillIntoCondition,
    getRefinedCausesHierarchy,
    (condition, refinedHierarchy) => {
        if (!condition) return [];
        return getLicensedConditionChildren(condition, refinedHierarchy);
    }
);

export const getChartDataRequestFilters = createSelector(
    getQueriedRefinementFiltersWithFallbackToDefaultFilters,
    getChartSelectedRefinementFilters,
    getQueriedConditionsRefinedGranularity,
    getEntityHierarchies,
    getSelectedChartType,
    getSelectedDatasetsConfig,
    getSelectedConditionsDataTypesGranularity,
    getSelectedConditionsDataTypes,
    isArrowChartDrillable,
    getDrillIntoCondition,
    getLicensedLevel2Causes,
    getLicensedDrillToConditions,
    getSelectedDataTool,
    (
        refinementFilters,
        chartFilters,
        refinedGranularity,
        entityHierarchies,
        chartType,
        datasetConfig,
        granularity,
        dataTypes = [],
        isArrowDrillable,
        drillIntoCondition,
        licensedLevel2Causes,
        licensedDrillToConditions,
        selectedDataTool
    ) => {
        if (!refinementFilters || !chartFilters) {
            return {};
        }

        const filters = { ...chartFilters };
        // TODO: @vova please look at this and add comment what is the requirement for this
        // @piotr for Covid-19 tool it's required to select 'observed data' forecast scenario without user action/notice
        // and these filter value are hidden from refinement filters options
        if (datasetConfig.autoSelectedChartFilters) {
            Object.entries(datasetConfig.autoSelectedChartFilters).map(([key, value]) => {
                if (
                    filters[key] &&
                    refinementFilters[key] &&
                    refinedGranularity &&
                    refinedGranularity[key]
                ) {
                    const validatedValue = intersection(
                        value,
                        refinementFilters[key],
                        refinedGranularity[key]
                    );
                    filters[key] = uniq(filters[key].concat(validatedValue));
                }
            });
        }

        if (datasetConfig.permanentChartFilters) {
            Object.entries(datasetConfig.permanentChartFilters).map(([key, value]) => {
                if (refinementFilters[key] && refinedGranularity && refinedGranularity[key]) {
                    if (isEmpty(filters[key])) {
                        filters[key] = (
                            !isEmpty(refinementFilters[key])
                                ? refinementFilters[key]
                                : refinedGranularity[key]
                        ).slice(0, 1);
                    }

                    filters[key] = uniq(filters[key].concat(value));
                }
            });
        }

        switch (chartType) {
            case TYPE_MAP_CHART: {
                const detailLevel = chartFilters[MAP_DETAIL_LEVEL_ID_KEY] ?? NATIONAL_LEVEL;
                const mapType = chartFilters[MAP_TYPE_ID_KEY] ?? config.globalLocationId;
                const rawRefinedLocations = refinedGranularity?.[LOCATION_ID_KEY];
                const locationsGranularityForMap =
                    selectedDataTool === DENGUE_SCENARIOS_PATH && rawRefinedLocations == null
                        ? []
                        : rawRefinedLocations;
                const locationIds = getLocationsForLevel(
                    detailLevel,
                    mapType,
                    locationsGranularityForMap,
                    entityHierarchies.locations
                );
                filters[LOCATION_ID_KEY] = locationIds;
                break;
            }

            case TYPE_TREEMAP_CHART:
                const dataType = chartFilters[DATA_TYPE_KEY];
                if (dataType) {
                    const primaryEntityKey = getDataTypePrimaryEntityKey(dataType);
                    filters[primaryEntityKey] = granularity[primaryEntityKey];
                }
                break;

            case TYPE_SCATTER_MAP_CHART:
                filters[LOCATION_ID_KEY] = refinedGranularity[LOCATION_ID_KEY];
                break;

            case TYPE_ARROW_CHART:
                if (isArrowDrillable && isEmpty(chartFilters[PRIMARY_ENTITY_KEY])) {
                    const causeKey = getDataTypePrimaryEntityKey(dataTypes[0]);
                    const isDrillIntoLevelSet = !!drillIntoCondition?.level;

                    const causes: DataCondition[] = isDrillIntoLevelSet
                        ? licensedLevel2Causes
                        : licensedDrillToConditions;

                    causes.forEach(({ primary_entity_id }) => {
                        if (isEmpty(filters[causeKey])) {
                            filters[causeKey] = [primary_entity_id];
                        } else {
                            filters[causeKey].push(primary_entity_id);
                        }
                    });
                }

                break;

            default:
                break;
        }

        if (
            filters?.[FORECAST_SCENARIO_ID_KEY]?.length &&
            (filters?.[FORECAST_SCENARIO_ID_KEY] || []).includes(config.referenceForecastScenarioId)
        ) {
            filters[FORECAST_SCENARIO_ID_KEY] = uniq([
                ...filters[FORECAST_SCENARIO_ID_KEY],
                config.pastEstimateScenarioId,
            ]);
        }

        // translate conditions into normal granularity filters
        if (!isEmpty(filters[PRIMARY_ENTITY_KEY])) {
            filters[PRIMARY_ENTITY_KEY].map(mapValueToCondition).forEach(
                ({ data_type, primary_entity_id }) => {
                    const primaryEntityKey = getDataTypePrimaryEntityKey(data_type);
                    if ((refinedGranularity[primaryEntityKey] || []).includes(primary_entity_id)) {
                        if (isEmpty(filters[primaryEntityKey])) {
                            filters[primaryEntityKey] = [primary_entity_id];
                        } else {
                            filters[primaryEntityKey].push(primary_entity_id);
                        }
                    }
                }
            );
        }

        const adoptedFilters = pick(
            intersection(Object.keys(filters), Object.keys(refinedGranularity)),
            filters
        );

        if (refinementFilters[OUTCOME_ID_KEY] && chartType === TYPE_MAP_CHART) {
            adoptedFilters[OUTCOME_ID_KEY] = refinementFilters[OUTCOME_ID_KEY];
        }

        return adoptedFilters;
    }
);

export const getMultipleSelectedRefinementFilterKeys = createSelector(
    getQueriedRefinementFiltersWithFallbackToDefaultFilters,
    getChartDataRequestFilters,
    (filters, chartFilters) => {
        const isMultiple = (value) => (value || '').toString().includes(',');

        const getMultipleValueKeys = (source) =>
            Object.entries(source)
                .filter(([key, value]) => isMultiple(value))
                .map(([key]) => key);

        return uniq([...getMultipleValueKeys(filters), ...getMultipleValueKeys(chartFilters)]);
    }
);

export const getMaxValuesSelectedFilter = createSelector(
    getSelectedRefinementFiltersWithFallbackToDefaultFilters,
    getChartDataRequestFilters,
    getSelectedConditions,
    (refinementFilters, chartFilters, conditions) => {
        const filters = { ...refinementFilters, ...chartFilters };
        const filterValuesAmountMap = {
            [PRIMARY_ENTITY_ID_LOCALIZATION_KEY]: conditions.length,
        };

        const getValuesAmount = (value) => (value || '').toString().split(',').length;

        Object.entries(filters).forEach(([key, value]) => {
            if (key !== FORECAST_SCENARIO_ID_KEY) {
                filterValuesAmountMap[key] = getValuesAmount(value);
            }
        });

        if (Object.values(filterValuesAmountMap).filter((amount) => amount > 1).length < 2) {
            return PRIMARY_ENTITY_ID_LOCALIZATION_KEY;
        }

        const maxValuesAmount = Math.max(...Object.values(filterValuesAmountMap));
        const maxValuesFilters = Object.entries(filterValuesAmountMap)
            .filter(([key]) => !isTimeUnitKey(key))
            .filter(([key, value]) => value === maxValuesAmount)
            .map(([key, value]) => key);

        if (
            isEmpty(maxValuesFilters) ||
            maxValuesFilters.includes(PRIMARY_ENTITY_ID_LOCALIZATION_KEY)
        ) {
            return PRIMARY_ENTITY_ID_LOCALIZATION_KEY;
        }

        return maxValuesFilters[0];
    }
);

export const getStaticForecastTimeStart = createSelector(
    getSelectedDatasetsConfig,
    getRoundId,
    getIncludeNowcasts,
    getOrganizationForecastDataCalculationIsAllowed,
    getSelectedDatasetForecastDataCalculationMap,
    getHasNowcastYearsBeyondPublished,
    (
        selectedDatasetConfig,
        roundId,
        includeNowcasts,
        isNowcastsEnabled,
        forecastDataCalculationMap,
        hasNowcastYearsBeyondPublished
    ): number => {
        const defaultValue = 0;

        if (
            checkIsNowcastsActive(
                isNowcastsEnabled,
                includeNowcasts,
                forecastDataCalculationMap,
                hasNowcastYearsBeyondPublished
            )
        ) {
            return getNowcastsStartYear(forecastDataCalculationMap) ?? defaultValue;
        }

        if (!selectedDatasetConfig) {
            return defaultValue;
        }

        const { static_forecast_start_times_map } = selectedDatasetConfig;
        if (!static_forecast_start_times_map) {
            return defaultValue;
        }

        return static_forecast_start_times_map[roundId] ?? defaultValue;
    }
);

export const isForecastingData = createSelector(
    getSelectedDatasetsConfig,
    getRoundId,
    getIncludeNowcasts,
    getOrganizationForecastDataCalculationIsAllowed,
    getSelectedDatasetForecastDataCalculationMap,
    getHasNowcastYearsBeyondPublished,
    (
        selectedDatasetConfig,
        roundId,
        includeNowcasts,
        isNowcastsEnabled,
        forecastDataCalculationMap,
        hasNowcastYearsBeyondPublished
    ): boolean => {
        if (!selectedDatasetConfig) {
            return false;
        }

        if (
            checkIsNowcastsActive(
                isNowcastsEnabled,
                includeNowcasts,
                forecastDataCalculationMap,
                hasNowcastYearsBeyondPublished
            )
        ) {
            return true;
        }

        const { rounds_with_forecast_data } = selectedDatasetConfig;
        if (!rounds_with_forecast_data) {
            return false;
        }

        return rounds_with_forecast_data.includes(roundId);
    }
);

export const isPopulationData = (state: RootState): boolean =>
    isDataIncludesDataType(state, PopulationRecords);

export const isGHTData = (state: RootState): boolean =>
    isDataIncludesDataType(state, HealthThreatsMetricsRecords);

export const isDengueData = (state: RootState): boolean =>
    isDataIncludesDataType(state, TakedaDengueRecords);

export const isDataIncludesDataType = (state: RootState, dataType: DataType): boolean => {
    const conditions = getSelectedConditions(state);
    const dataTypes = conditions.map(({ data_type }) => data_type);
    return dataTypes.includes(dataType);
};

export const getCdnRestrictions = createSelector(getConditionDetailNotes, (cdnByCondition) => {
    if (cdnByCondition == null) {
        return null;
    }

    const restrictionsByCondition = cdnByCondition.map((condition) => {
        const result: ConditionRestrictionRecord = {} as any;

        condition.metaRecords.forEach((record) => {
            switch (record.key) {
                case 'data_type':
                    result.data_type = record.value as string;
                    break;
                case 'primary_entity_id':
                    result.primary_entity_id = record.value as number;
                    break;
                default:
                    break;
            }
        });

        condition.records.forEach((record) => {
            switch (record.key) {
                case 'non_fatal_data_source_link':
                case 'fatal_data_source_link':
                    result[record.key] = record.value as string;
                    break;

                case 'fatal_female_age_group_restriction_ids':
                case 'non_fatal_female_age_group_restriction_ids':
                case 'fatal_male_age_group_restriction_ids':
                case 'non_fatal_male_age_group_restriction_ids':
                case 'fatal_location_restriction_ids':
                case 'non_fatal_location_restriction_ids':
                    result[record.key] = record.value as number[];
                    break;

                default:
                    break;
            }
        });

        return result;
    });

    return restrictionsByCondition;
});

export const getMultipleValuesQueriedRefinementFilters = createSelector(
    getQueriedConditions,
    getQueriedConditionsRefinedGranularity,
    getQueriedRefinementFiltersWithFallbackToDefaultFilters,
    (
        conditions,
        queriedConditionsRefinedGranularity,
        queriedRefinementFiltersWithFallbackToDefaultFilters
    ): DataFilters | {} => {
        const multipleValuesFilters = {};
        if (conditions && conditions.length > 1) {
            multipleValuesFilters[PRIMARY_ENTITY_ID_KEY] = conditions;
        }

        const refinedGranularity = queriedConditionsRefinedGranularity || {};
        const isMulti = ([key, value]): boolean => {
            const granularityValues = refinedGranularity[key] || [];
            return intersection(value, granularityValues).length > 1;
        };

        Object.entries(queriedRefinementFiltersWithFallbackToDefaultFilters)
            .filter(isMulti)
            .forEach(([key, value]) => {
                multipleValuesFilters[key] = value;
            });

        return multipleValuesFilters;
    }
);

export const hasErrorInRefinements = createSelector(
    getSelectedConditionsRefinedGranularity,
    getSelectedConditions,
    getSelectedRefinementFiltersWithFallbackToDefaultFilters,
    (refinedGranularity, selectedConditions, selectedRefinementFilters): boolean => {
        return hasErrorInRefinementFilters({
            granularity: refinedGranularity,
            selectedConditions,
            selectedRefinementFilters,
        });
    }
);

export const hasFiltersChangedSinceLastRequest = (state: RootState): boolean => {
    return state.dataExplorer.filtersChangedSinceLastRequest;
};

export const getChartSelectedFiltersGranularity = createSelector(
    getQueriedConditions,
    getQueriedRefinementFiltersWithFallbackToDefaultFilters,
    getSelectedConditionsRefinedGranularity,
    (
        conditions,
        queriedRefinementFiltersWithFallbackToDefaultFilters,
        granularity
    ): DataFilters | {} => {
        const multipleValuesFilters = {};
        if (conditions && conditions.length > 1) {
            multipleValuesFilters[PRIMARY_ENTITY_ID_KEY] = conditions;
        }
        const isMulti = ([key, value]): boolean => (value || []).length > 1;

        const dataFilters = intersectRefinementFiltersWithGranularity(
            queriedRefinementFiltersWithFallbackToDefaultFilters,
            granularity
        );
        Object.entries(dataFilters)
            .filter(isMulti)
            .forEach(([key, value]) => {
                multipleValuesFilters[key] = value;
            });

        return multipleValuesFilters;
    }
);

export const hasCdnRestrictions = createSelector(
    [getSelectedRefinementFiltersWithFallbackToDefaultFilters, getCdnRestrictions],
    (selectedRefinementFilters, cdnRestrictions) => {
        const FATAL_MEASURES = [Deaths, YLLs];
        const NON_FATAL_MEASURES = [YLDs, Incidence, Prevalence];
        const GENDER = {
            Male: 1,
            Female: 2,
            Both: 3,
        };

        /**
         * null checks
         */
        if (selectedRefinementFilters == null || cdnRestrictions == null) {
            return false;
        }

        /**
         * destructuring data
         */
        const { measure_id, gender_id, age_group_id, location_id } = selectedRefinementFilters;

        const selectedMeasureIds = measure_id || [];
        const selectedGenderIds = gender_id || [];
        const selectedAgeGroupIds = age_group_id || [];
        const selectedLocationIds = location_id || [];

        /**
         * conditions helpers
         */
        const hasFatalMeasures =
            selectedMeasureIds.length > 0 &&
            selectedMeasureIds.some((id) => FATAL_MEASURES.includes(id));
        const hasNonFatalMeasures =
            selectedMeasureIds.length > 0 &&
            selectedMeasureIds.some((id) => NON_FATAL_MEASURES.includes(id));
        const hasDalyMeasure =
            selectedMeasureIds.length > 0 && selectedMeasureIds.some((id) => [DALYs].includes(id));

        const hasMaleGender =
            selectedGenderIds.length > 0 &&
            selectedGenderIds.some((id) => [GENDER.Male].includes(id));
        const hasFemaleGender =
            selectedGenderIds.length > 0 &&
            selectedGenderIds.some((id) => [GENDER.Female].includes(id));
        const hasBothGender =
            selectedGenderIds.length > 0 &&
            selectedGenderIds.some((id) => [GENDER.Both].includes(id));

        /**
         * generic helpers
         */
        const somePresentIn = (selectedFilterIds) => (restrictedIds) =>
            selectedFilterIds.some((id) => restrictedIds.includes(id));

        /**
         * concrete helpers
         */
        const anySelectedLocationsPresentIn = somePresentIn(selectedLocationIds);
        const anySelectedAgeGroupsPresentIn = somePresentIn(selectedAgeGroupIds);

        // restriction calculation => restriction is true when at least one restriction of any filter is fulfilled
        const foundAtLeastOneRestriction = cdnRestrictions.some((conditionRestriction) => {
            let {
                fatal_female_age_group_restriction_ids,
                fatal_location_restriction_ids,
                fatal_male_age_group_restriction_ids,
                non_fatal_location_restriction_ids,
                non_fatal_female_age_group_restriction_ids,
                non_fatal_male_age_group_restriction_ids,
            } = conditionRestriction;

            fatal_female_age_group_restriction_ids = fatal_female_age_group_restriction_ids || [];
            fatal_location_restriction_ids = fatal_location_restriction_ids || [];
            fatal_male_age_group_restriction_ids = fatal_male_age_group_restriction_ids || [];
            non_fatal_location_restriction_ids = non_fatal_location_restriction_ids || [];
            non_fatal_female_age_group_restriction_ids =
                non_fatal_female_age_group_restriction_ids || [];
            non_fatal_male_age_group_restriction_ids =
                non_fatal_male_age_group_restriction_ids || [];

            if (hasNonFatalMeasures) {
                if (
                    hasFemaleGender &&
                    anySelectedAgeGroupsPresentIn(non_fatal_female_age_group_restriction_ids)
                ) {
                    return true;
                } else if (
                    hasMaleGender &&
                    anySelectedAgeGroupsPresentIn(non_fatal_male_age_group_restriction_ids)
                ) {
                    return true;
                } else if (
                    hasBothGender &&
                    anySelectedAgeGroupsPresentIn(
                        intersection(
                            non_fatal_female_age_group_restriction_ids,
                            non_fatal_male_age_group_restriction_ids
                        )
                    )
                ) {
                    return true;
                }
                if (anySelectedLocationsPresentIn(non_fatal_location_restriction_ids)) {
                    return true;
                }
            }
            if (hasFatalMeasures) {
                if (
                    hasFemaleGender &&
                    anySelectedAgeGroupsPresentIn(fatal_female_age_group_restriction_ids)
                ) {
                    return true;
                } else if (
                    hasMaleGender &&
                    anySelectedAgeGroupsPresentIn(fatal_male_age_group_restriction_ids)
                ) {
                    return true;
                } else if (
                    hasBothGender &&
                    anySelectedAgeGroupsPresentIn(
                        intersection(
                            fatal_female_age_group_restriction_ids,
                            fatal_male_age_group_restriction_ids
                        )
                    )
                ) {
                    return true;
                }
                if (anySelectedLocationsPresentIn(fatal_location_restriction_ids)) {
                    return true;
                }
            }
            if (hasDalyMeasure) {
                if (
                    hasFemaleGender &&
                    anySelectedAgeGroupsPresentIn(
                        intersection(
                            fatal_female_age_group_restriction_ids,
                            non_fatal_female_age_group_restriction_ids
                        )
                    )
                ) {
                    return true;
                } else if (
                    hasMaleGender &&
                    anySelectedAgeGroupsPresentIn(
                        intersection(
                            fatal_male_age_group_restriction_ids,
                            non_fatal_male_age_group_restriction_ids
                        )
                    )
                ) {
                    return true;
                } else if (
                    hasBothGender &&
                    anySelectedAgeGroupsPresentIn(
                        intersection(
                            fatal_female_age_group_restriction_ids,
                            fatal_male_age_group_restriction_ids,
                            non_fatal_female_age_group_restriction_ids,
                            non_fatal_male_age_group_restriction_ids
                        )
                    )
                ) {
                    return true;
                }
                if (
                    anySelectedLocationsPresentIn(
                        intersection(
                            fatal_location_restriction_ids,
                            non_fatal_location_restriction_ids
                        )
                    )
                ) {
                    return true;
                }
            }
        });

        return foundAtLeastOneRestriction;
    }
);

export const getSelectedDatasetsConfigByConditions =
    (conditions: DataCondition[]) => (state: RootState) => {
        const selectedConditionsDataTypes = getConditionsDataTypes(conditions);
        const selectedDataCollection = getSelectedDataCollectionForDataTool(state);

        return getSelectedDatasetsConfiguration(
            selectedConditionsDataTypes,
            selectedDataCollection
        );
    };

export const getChartFiltersToOmit = (state: RootState): DataGranularityKey[] =>
    state.dataExplorer.omittedChartFilters;

export const getRefinedLocationChildrenById = createSelector(
    getLocationChildrenById,
    getSelectedConditionsRefinedGranularity,
    (locationChildrenById, refinedGranularity): Record<number, LocationEntity[]> | null => {
        if (!(locationChildrenById && refinedGranularity?.[LOCATION_ID_KEY])) {
            return null;
        }

        const refinedLocationGranularity = refinedGranularity[LOCATION_ID_KEY];

        const refinedLocationChildrenById = {};
        Object.entries(locationChildrenById).forEach(([key, value]) => {
            if (value && value.length) {
                const ids = intersection(
                    value.map(({ id }) => id),
                    refinedLocationGranularity
                );
                refinedLocationChildrenById[key] = ids && ids.length ? ids : null;
            }
        });

        return refinedLocationChildrenById;
    }
);

export const getSelectedChartConditions = createSelector(
    getChartSelectedRefinementFilters,
    (chartFilters) => {
        return (chartFilters?.[PRIMARY_ENTITY_KEY] || []).map(mapValueToCondition);
    }
);

export const getAvailableToSelectConditions = createSelector(
    getSelectedDataCollectionForDataTool,
    getSelectedDataType,
    getSelectedConditions,
    (selectedDataCollection, selectedDataType, selectedConditions): DataCondition[] | null => {
        if (!selectedDataCollection) {
            return null;
        }

        const datasets = selectedDataCollection.datasets.filter(
            (i) => selectedDataType === AllDataTypes || selectedDataType === i.data_type
        );

        const selectedConditionsIterators = selectedConditions.map(conditionComparisonFunc);

        const allConditions = uniqBy(
            conditionComparisonFunc,
            flatten(datasets.map(getDatasetConditions))
        );

        return allConditions.filter(
            (condition) => !selectedConditionsIterators.includes(conditionComparisonFunc(condition))
        );
    }
);

export const getDataTableDisplayState = (state: RootState): DataTableDisplayState =>
    state.dataExplorer.dataTableDisplayState;

export const getDataTableVisibleColumns = createSelector(
    getDataTableDisplayState,
    (state) => state.visibleColumns
);

export const getDataTableSortedByColumn = createSelector(
    getDataTableDisplayState,
    (state) => state.sortedByColumn
);

export const getDataTableSortedDescending = createSelector(
    getDataTableDisplayState,
    (state) => state.sortedDescending
);

export const getCustomChartSettings = (state: RootState): Record<string, any> =>
    state.dataExplorer.customChartSettings;

export const getSelectionHasNullComorbidity = createSelector(
    getSelectedConditionsRefinedGranularity,
    getSelectedConditionsDataTypesGranularity,
    getSelectedConditions,
    getSelectedRefinementFiltersWithFallbackToDefaultFilters,
    (refinedGranularity, granularity, selectedConditions, selectedRefinementFilters): boolean => {
        const fallbackGranularity = refinedGranularity || granularity;
        return hasErrorInRefinementFilters({
            granularity: { [COMORBID_CAUSE_ID_KEY]: fallbackGranularity[COMORBID_CAUSE_ID_KEY] },
            selectedConditions,
            selectedRefinementFilters,
        });
    }
);

const getCombinedFiltersCountInputs = createSelector(
    getSelectedConditions,
    getSelectedRefinementFiltersWithFallbackToDefaultFilters,
    getSelectedConditionsRefinedGranularity,
    getSelectedDataCollectionForDataTool,
    getOrganization,
    getRoundId,
    getIsNowcastsActive,
    getSelectedConditionsLastPublishedYear,
    getSelectionHasNullComorbidity,
    (
        conditions,
        filters,
        granularity,
        dataCollection,
        organization,
        roundId,
        isNowcastsActive,
        lastPublishedYear,
        hasNullComorbidity
    ): CombinedFiltersCountInputs => ({
        conditions,
        filters,
        granularity,
        dataCollection,
        organization,
        roundId,
        isNowcastsActive,
        lastPublishedYear,
        hasNullComorbidity,
    })
);

export const calculateCombinedFiltersCount = (
    state: RootState,
    {
        ignoreTimeUnits,
        ...overrides
    }: CombinedFiltersCountOverrides & CombinedFiltersCountOptions = {}
): number =>
    countCombinedFilters(
        { ...getCombinedFiltersCountInputs(state), ...overrides },
        {
            ignoreTimeUnits,
        }
    );

export const getCombinedSelectedFiltersCount = createSelector(
    getCombinedFiltersCountInputs,
    (inputs) => countCombinedFilters(inputs)
);

export const getCombinedQueriedFiltersWithoutTimeUnitsCount = (state: RootState): number =>
    state.dataExplorer.combinedQueriedFiltersWithoutTimeUnitsCount;

export const getDengueCalculations = (state: RootState) => state.dataExplorer.dengueCalculations;

export const getDengueShowAvertedOutcome = createSelector(
    getDengueCalculations,
    (dengueCalculations) => dengueCalculations.showAvertedOutcome
);

export const getDengueShowCumulativeOutcome = createSelector(
    getDengueCalculations,
    (dengueCalculations) => dengueCalculations.showCumulativeOutcome
);

export const getDengueBaselineData = createSelector(
    getDengueCalculations,
    (dengueCalculations) => dengueCalculations.baselineData
);

export const getDengueIsLoadingBaseline = createSelector(
    getDengueCalculations,
    (dengueCalculations) => dengueCalculations.isLoadingBaseline
);
